import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { CallRemarksDialog } from './CallRemarksDialog';
import { refreshCallLists } from '@/features/ivr/refreshCallLists';

interface Props {
  toNumber: string;
  orderId?: string | number;
  awbNo?: string;
  lrNo?: string;
  reason?: string;
  size?: 'sm' | 'md';
  className?: string;
  label?: string;
}

export function CallButton({ toNumber, orderId, awbNo, lrNo, reason, size = 'sm', className = '', label }: Props) {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const qc = useQueryClient();
  if (!toNumber) return null;

  // Every click-to-call on the platform comes through here — order lists, order detail (B2B and
  // B2C) and leads — so asking for the remark in this one place covers all of them.
  const handle = (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    setAsking(true);
  };

  const place = async (remarks: string) => {
    setBusy(true);
    try {
      const r = await api.post('/ivr/call', { toNumber, orderId: orderId ? String(orderId) : undefined, awbNo, lrNo, reason, remarks: remarks || undefined });
      if (r.data?.ok) toast.success(`Call initiated to ${toNumber}`);
      else toast.error(`Call failed: ${r.data?.providerResponse ?? 'unknown'}`);
      setAsking(false);
      // The new call and its remark appear at once on every call list on screen — no reload.
      refreshCallLists(qc);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Call failed');
    } finally { setBusy(false); }
  };
  const sz = size === 'sm' ? 'p-1 text-xs' : 'px-2 py-1 text-sm';
  return (
    <>
      <button onClick={handle} disabled={busy}
        title={`Click to call ${toNumber}`}
        className={`inline-flex items-center gap-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition disabled:opacity-50 ${sz} ${className}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Phone size={12} />}
        {label}
      </button>
      <CallRemarksDialog open={asking} toNumber={toNumber} awbNo={awbNo ?? lrNo} busy={busy}
        onCancel={() => setAsking(false)} onConfirm={place} />
    </>
  );
}
